import { FormHelperText, Grid, Tooltip } from '@material-ui/core';
import { Field, Form } from 'formik';
import moment from 'moment';
import * as React from 'react';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { useUsersModifiedProduct } from 'v2/hooks/useUsersModifiedProduct';

import { useStyles } from './styles';
import { CHANGE_HISTORY_FEATURE_RELEASE_DATE, calendarPreset } from '../../constants';

import type { ChangeHistoryFilterFormValues, ProductData } from '../../constants';
import type { FormikProps } from 'formik';
import type { Moment } from 'moment';

interface ChangeHistoryFilterFormProps extends FormikProps<ChangeHistoryFilterFormValues> {
  setFilters: (filter: ChangeHistoryFilterFormValues) => void;
  productData: ProductData;
}

function ChangeHistoryFilterForm({ values, errors, setFieldValue, setFilters, productData }: ChangeHistoryFilterFormProps) {
  const classes = useStyles();

  const [editedAtDate, editedAtFinalDate] = values.dates;

  const isOutsideRange = (day: Moment) => {
    const isAfterToday = day.isAfter(moment());
    const isAfterMaxRange = day.isAfter(moment(editedAtDate, 'DD/MM/YYYY').add(180, 'days'));
    const isBeforeFeatureRelease = day.isBefore(moment(CHANGE_HISTORY_FEATURE_RELEASE_DATE, 'DD/MM/YYYY'));

    return isAfterToday || isAfterMaxRange || isBeforeFeatureRelease;
  };

  const userModifiedProductQuery = useUsersModifiedProduct({
    productId: productData.id,
    filter: {
      edited_at: editedAtDate,
      edited_at_final: editedAtFinalDate,
    },
  });

  const userModifierOptions = userModifiedProductQuery.data?.map((user) => ({ label: user.name, value: user.id })) || [];

  React.useEffect(() => {
    const isFirstDateBeforeFeatureRelease = moment(editedAtDate, 'DD/MM/YYYY').isBefore(moment(CHANGE_HISTORY_FEATURE_RELEASE_DATE, 'DD/MM/YYYY'));

    if (isFirstDateBeforeFeatureRelease) setFieldValue('dates', [CHANGE_HISTORY_FEATURE_RELEASE_DATE, editedAtFinalDate]);

    const isMaxRangeExceeded = moment(editedAtFinalDate, 'DD/MM/YYYY').isAfter(moment(editedAtDate, 'DD/MM/YYYY').add(179, 'days'));
    if (!isMaxRangeExceeded) {
      setFilters(values);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, errors]);

  return (
    <Form>
      <div className={classes.formContainer}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Field
              id='user_modifier'
              name='user_modifier'
              displayEmpty={true}
              options={[{ label: 'Busque por pessoa', value: '', disabled: true }, ...userModifierOptions]}
              style={{ border: 'rgb(219, 219, 219)', borderRadius: '0', height: '40px' }}
              variant='outlined'
              loading={userModifiedProductQuery.isLoading}
              component={SelectFormField}
              fullWidth
            />
          </Grid>
          <Tooltip
            title={`Apenas serão exibidos registros de alterações realizadas a partir de ${CHANGE_HISTORY_FEATURE_RELEASE_DATE}`}
            placement='top'
          >
            <Grid item xs={12} sm={6}>
              <Field
                id='calendar'
                label=''
                name='dates'
                customPreset={calendarPreset}
                component={DateRangePickerFormField}
                isOutsideRange={isOutsideRange}
                maxDate={moment()}
              />
              {errors.dates && <FormHelperText error>{errors.dates}</FormHelperText>}
            </Grid>
          </Tooltip>
        </Grid>
      </div>
    </Form>
  );
}

export { ChangeHistoryFilterForm };
